import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../../components/carbon/DataTable/DataTable";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import ConfirmDialog from "../../../components/carbon/ConfirmDialog/ConfirmDialog";
import {
  useDeleteUserGroup,
  useUserGroups,
} from "../../../hooks/useUserGroups";
import { useNotification } from "../../../context";
import { UserGroup } from "../../../types";
import UserGroupForm from "./UserGroupForm";
import "./userGroups.scss";

interface UserGroupRow extends DataTableRow {
  id: string;
  name: string;
  memberCount: number;
  actions: ReactNode;
}

/**
 * The Groups tab inside `/users` (see `Users.tsx`), rebuilt on the Carbon primitives (Task 13) in
 * place of a Tailwind table plus a headlessui `Slide` panel. Has no route of its own; it's also
 * NOT the same thing as `hooks/useUserGroups.ts`, which `PurchaseRequestV2Detail.tsx` imports
 * directly (`useMyUserGroups`, for its own peer-permission check) without ever rendering this
 * component — that hook module is untouched here, so that consumer's contract is unaffected by
 * this rewrite. Still verified in a browser as part of this task, since a shared module is
 * exactly the kind of thing a rewrite silently breaks.
 *
 * Uses a local `.user-groups__header` row rather than the primitives layer's `PageHeader`: this
 * view is a tab's content, not a page, and `Users.tsx` already renders its own page-level
 * `PageHeader` (an `<h1>`) above the tab strip regardless of which tab is selected — reusing
 * `PageHeader` here would put two `<h1>`s on the page at once whenever Groups is selected.
 */
const UserGroups = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const { data: groups, isLoading } = useUserGroups();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<UserGroup | null>(null);
  const [pendingDelete, setPendingDelete] = useState<UserGroup | null>(null);

  const deleteGroup = useDeleteUserGroup(
    () => setPendingDelete(null),
    (error: any) =>
      openNotification(
        error?.response?.data?.message || t("userGroups.deleteError")
      )
  );

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (group: UserGroup) => {
    setEditing(group);
    setFormOpen(true);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibleGroups = groups ?? [];

  // Unlike Users.tsx's own `isTrulyEmpty`, there is no search here to distinguish "search
  // matched nothing" from "there is genuinely nothing yet" — this tab has never had a search box
  // — so an empty roster is unconditionally the "nothing yet" case. This is the first screen in
  // the flow where that branch is actually reachable: the Users roster can never be empty (the
  // viewer is always in it), so Users.tsx's own copy of this pattern has never been exercised. A
  // company with zero groups is real and reachable, which is why this file's test suite drives
  // it directly rather than inheriting the pattern unverified — see this task's report.
  const isTrulyEmpty = !isLoading && visibleGroups.length === 0;

  const addAction = <Button onClick={openNew} renderIcon={Add}>{t("userGroups.add")}</Button>;

  const rows: UserGroupRow[] = useMemo(
    () =>
      visibleGroups.map((group) => ({
        id: group.id,
        name: group.name,
        memberCount: group.memberCount,
        actions: (
          <div className="user-groups__row-actions">
            <Button kind="ghost" size="sm" onClick={() => openEdit(group)}>
              {t("userGroups.edit")}
            </Button>
            <Button
              kind="danger--ghost"
              size="sm"
              onClick={() => setPendingDelete(group)}
            >
              {t("userGroups.delete")}
            </Button>
          </div>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleGroups, t]
  );

  const headers: DataTableColumn<UserGroupRow>[] = useMemo(
    () => [
      { key: "name", header: t("userGroups.name") },
      { key: "memberCount", header: t("userGroups.members") },
      { key: "actions", header: "" },
    ],
    [t]
  );

  return (
    <div>
      <div className="user-groups__header">
        <h2 className="user-groups__title">{t("userGroups.title")}</h2>
        {!isTrulyEmpty ? addAction : null}
      </div>

      <DataTable
        headers={headers}
        rows={rows}
        isLoading={isLoading}
        empty={
          <EmptyState title={t("userGroups.noGroups")} action={addAction} />
        }
      />

      <UserGroupForm group={editing} open={formOpen} setOpen={setFormOpen} />

      <ConfirmDialog
        open={!!pendingDelete}
        danger
        title={t("userGroups.deleteTitle")}
        body={t("userGroups.deleteQuestion")}
        confirmLabel={t("userGroups.delete")}
        onConfirm={() => pendingDelete && deleteGroup.mutate(pendingDelete.id)}
        onCancel={() => setPendingDelete(null)}
        isPending={deleteGroup.isPending}
      />
    </div>
  );
};

export default UserGroups;
